import React, {useEffect, useRef, useState} from 'react'
import {useFieldArray, useForm} from 'react-hook-form'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {toast} from 'react-toastify'
import type {CSSObjectWithLabel} from 'react-select'
import {PageTitle} from 'layout/master-layout'
import Spinner from 'layout/common/Spinner'
import {
 Button,
 FileField,
 KTSVG,
 Select2DropdownField,
 TextField,
} from 'helpers'
import {KTConfirmPopup} from 'helpers/ui-components/KTConfirmPopup'
import {IFileObject, IFormField} from 'models/Common'
import {
 CreateCropPayload,
 CropQualityParameterUpdatePayload,
 CropVarietyUpdatePayload,
 UpdateCropPayload,
} from 'models/CropMaster'
import {cropCategoryOptions} from 'constants/common'
import {
 createCrop,
 deleteCropQualityParameter,
 fetchCropDetails,
 fetchQualityParameters,
 updateCrop,
} from 'utils/api/cropMasterService'
import {handleFileChange} from 'utils/helpers/fileUpload'
import QualityParameterModal, {
 IEditableQualityParameter,
 MAX_QUALITY_PARAMETERS,
} from './QualityParameterModal'
import {getCropMasterPath} from './cropMasterRoutes'

type IProps = {
 cropId: number | null
}

type IVarietyRow = {
 id?: number
 name: string
}

const HSN_CODE_PATTERN = /^\d{8}$/

type IOriginalCropSnapshot = {
 crop_name: string
 crop_category: string
 hsn_code: string
 lingo: string
 image_url: string | null
 varietiesJSON: string
 qualityParametersJSON: string
}

const formatParameterValue = (
 qp: IEditableQualityParameter
) => {
 const {type, unit, default_value: defaultValue} = qp
 if (!defaultValue) return '-'
 const unitSuffix = unit ? ` ${unit}` : ''

 if (type === 'RANGE') {
  const min = defaultValue.min ?? '-'
  const max = defaultValue.max ?? '-'
  return `${min} - ${max}${unitSuffix}`
 }
 if (type === 'FREE_TEXT') {
  const value = defaultValue.value
  return value !== undefined &&
   value !== null &&
   value !== ''
   ? `${value}${unitSuffix}`
   : '-'
 }
 if (type === 'DROPDOWN') {
  return defaultValue.label
   ? String(defaultValue.label)
   : '-'
 }

 if ('min' in defaultValue || 'max' in defaultValue) {
  const min = defaultValue.min ?? '-'
  const max = defaultValue.max ?? '-'
  return `${min} - ${max}${unitSuffix}`
 }
 if (defaultValue.label) return String(defaultValue.label)
 if (
  defaultValue.value !== undefined &&
  defaultValue.value !== null
 ) {
  return String(defaultValue.value)
 }
 return '-'
}

const SECTION_CARD_CLASS =
 'w-full border border-[#E6EBF0] rounded-xl p-6 bg-[#FAFAFA]'

const FIELD_MIN_HEIGHT_CLASS =
 'min-h-[64px] flex flex-col justify-center'

const VARIETY_FIELD_MIN_HEIGHT_CLASS =
 'min-h-[48px] flex flex-col justify-center'

const getFilledStateClass = (hasValue: boolean) =>
 hasValue ? '!bg-[#F5F8FA]' : '!bg-white'

const getFieldInputWrapperClass = (hasValue: boolean) =>
 `${FIELD_MIN_HEIGHT_CLASS} ${getFilledStateClass(
  hasValue
 )}`

const getVarietyFieldInputWrapperClass = (
 hasValue: boolean
) =>
 `${VARIETY_FIELD_MIN_HEIGHT_CLASS} ${getFilledStateClass(
  hasValue
 )}`

const SELECT_FIELD_RESET_STYLE = {
 transition: 'unset',
 boxShadow: 'unset',
 border: '0px',
 borderRadius: '0px',
 padding: '0px 0px 0px 0px',
 minHeight: 'auto',
}

const AddCropPage = ({cropId}: IProps) => {
 const {t} = useTranslation()
 const navigate = useNavigate()
 const isEditMode = Boolean(cropId)

 const [isDisabled, setIsDisabled] = useState(isEditMode)
 const [loader, setLoader] = useState(false)
 const [uploadedFileData, setUploadedFileData] =
  useState<IFileObject>({})
 const [qualityParameters, setQualityParameters] = useState<
  IEditableQualityParameter[]
 >([])
 const [
  showQualityParameterModal,
  setShowQualityParameterModal,
 ] = useState(false)

 const [
  editingQualityParameterIndex,
  setEditingQualityParameterIndex,
 ] = useState<number | null>(null)

 const [
  pendingDeleteQualityParameterIndex,
  setPendingDeleteQualityParameterIndex,
 ] = useState<number | null>(null)

 const originalCropRef =
  useRef<IOriginalCropSnapshot | null>(null)

 const {
  register,
  control,
  reset,
  watch,
  handleSubmit,
  formState: {errors, isValid},
 } = useForm({
  mode: 'all',
  reValidateMode: 'onChange',
  defaultValues: {
   crop_name: '',
   lingo: '',
   crop_category: '',
   hsn_code: '',
   varieties: [] as IVarietyRow[],
  },
 })

 const {
  fields: varietyFields,
  append: appendVariety,
  remove: removeVariety,
 } = useFieldArray({control, name: 'varieties'})

 const watchedCropName = watch('crop_name')
 const watchedLingo = watch('lingo')
 const watchedCropCategory = watch('crop_category')
 const watchedHsnCode = watch('hsn_code')

 useEffect(() => {
  const loadCropDetails = async () => {
   if (!cropId) return
   setLoader(true)
   const [res, qualityParameterDefinitions] =
    await Promise.all([
     fetchCropDetails(cropId),
     fetchQualityParameters(),
    ])
   const definitions = Array.isArray(
    qualityParameterDefinitions
   )
    ? qualityParameterDefinitions
    : []
   if (res) {
    const varieties: IVarietyRow[] = (
     res.varieties ?? []
    ).map((variety) => ({
     id: variety.id,
     name: variety.name,
    }))
    const editableQualityParameters: IEditableQualityParameter[] =
     (res.quality_parameters ?? []).map((qp) => {
      const qualityParameterId = (qp.quality_parameter_id ??
       qp.quality_parameter?.id) as number
      const definition = definitions.find(
       (def) => def.id === qualityParameterId
      )
      return {
       id: qp.id,
       quality_parameter_id: qualityParameterId,
       quality_parameter_name:
        qp.quality_parameter?.name ??
        definition?.name ??
        '',
       type: qp.quality_parameter?.type ?? definition?.type,
       unit: qp.quality_parameter?.unit ?? definition?.unit,
       default_value: qp.default_value,
      }
     })

    reset({
     crop_name: res.crop_name ?? '',
     lingo: res.lingo ?? '',
     crop_category: res.crop_category ?? '',
     hsn_code: res.hsn_code ?? '',
     varieties,
    })
    setQualityParameters(editableQualityParameters)
    if (res.image_url) {
     setUploadedFileData({
      crop_image: {
       filePath: res.image_url,
       fileName: res.image_url,
       fileType: 'image',
       signedUrl: res.image_url,
      },
     })
    }

    originalCropRef.current = {
     crop_name: res.crop_name ?? '',
     crop_category: res.crop_category ?? '',
     hsn_code: res.hsn_code ?? '',
     lingo: res.lingo ?? '',
     image_url: res.image_url ?? null,
     varietiesJSON: JSON.stringify(
      varieties.map((v) => ({id: v.id, name: v.name}))
     ),
     qualityParametersJSON: JSON.stringify(
      editableQualityParameters.map((qp) => ({
       id: qp.id,
       quality_parameter_id: qp.quality_parameter_id,
       default_value: qp.default_value,
      }))
     ),
    }
   }
   setLoader(false)
  }
  void loadCropDetails()
 }, [cropId])

 const handleEditBtnClick = () => {
  setIsDisabled(false)
 }

 const handleAddVariety = () => {
  appendVariety({name: ''})
 }

 const handleRemoveVariety = (index: number) => {
  removeVariety(index)
 }

 const handleSaveQualityParameters = (
  parameters: IEditableQualityParameter[]
 ) => {
  if (editingQualityParameterIndex !== null) {
   const [updated] = parameters
   setQualityParameters((prev) =>
    prev.map((qp, i) =>
     i === editingQualityParameterIndex ? updated : qp
    )
   )
  } else {
   setQualityParameters((prev) => [...prev, ...parameters])
  }
 }

 const handleRemoveCropImage = () => {
  setUploadedFileData((prev) => {
   const updated = {...prev}
   delete updated.crop_image
   return updated
  })
 }

 const handleCloseQualityParameterModal = () => {
  setShowQualityParameterModal(false)
  setEditingQualityParameterIndex(null)
 }

 const handleOpenAddQualityParameter = () => {
  setEditingQualityParameterIndex(null)
  setShowQualityParameterModal(true)
 }

 const handleOpenEditQualityParameter = (index: number) => {
  setEditingQualityParameterIndex(index)
  setShowQualityParameterModal(true)
 }

 const handleDeleteQualityParameterClick = (
  index: number
 ) => {
  setPendingDeleteQualityParameterIndex(index)
 }

 const handleConfirmDeleteQualityParameter = async (
  confirmed: boolean
 ) => {
  const index = pendingDeleteQualityParameterIndex
  setPendingDeleteQualityParameterIndex(null)
  if (!confirmed || index === null) return

  const qp = qualityParameters[index]
  if (!qp) return

  if (qp.id) {
   setLoader(true)
   const success = await deleteCropQualityParameter(qp.id)
   setLoader(false)
   if (!success) return
  }
  setQualityParameters((prev) =>
   prev.filter((item) => item !== qp)
  )
 }

 const getVarietiesPayload = (
  formData: IFormField
 ): CropVarietyUpdatePayload[] => {
  const varieties: IVarietyRow[] = formData.varieties ?? []
  return varieties
   .filter((variety) => variety.name?.trim())
   .map((variety) => ({
    ...(variety.id && {id: variety.id}),
    name: variety.name.trim(),
   }))
 }

 const getQualityParametersPayload =
  (): CropQualityParameterUpdatePayload[] =>
   qualityParameters.map((qp) => ({
    ...(qp.id && {id: qp.id}),
    quality_parameter_id: qp.quality_parameter_id,
    default_value: qp.default_value,
   }))

 const validateBeforeSubmit = (): boolean => {
  const imageUrl = uploadedFileData?.crop_image?.filePath
  if (imageUrl && imageUrl.length > 255) {
   toast.error(
    `${t('cropMaster.addCrop.uploadImage')} ${t(
     'helperText.plsEnterValid'
    )}`
   )
   return false
  }
  if (qualityParameters.length > MAX_QUALITY_PARAMETERS) {
   toast.error(
    t('cropMaster.addCrop.maxQualityParametersError', {
     max: MAX_QUALITY_PARAMETERS,
    })
   )
   return false
  }
  return true
 }

 const buildCreatePayload = (
  formData: IFormField
 ): CreateCropPayload => ({
  crop_name: formData.crop_name,
  crop_category: formData.crop_category,
  hsn_code: formData.hsn_code || null,
  lingo: formData.lingo || null,
  ...(uploadedFileData?.crop_image?.filePath && {
   image_url: uploadedFileData.crop_image.filePath,
  }),
  varieties: getVarietiesPayload(formData).map(
   (variety) => ({name: variety.name})
  ),
  quality_parameters: getQualityParametersPayload().map(
   (qp) => ({
    quality_parameter_id: qp.quality_parameter_id,
    default_value: qp.default_value,
   })
  ),
 })

 const buildUpdatePayload = (
  formData: IFormField
 ): UpdateCropPayload => {
  const original = originalCropRef.current
  const payload: UpdateCropPayload = {}
  if (!original) return payload

  if (formData.crop_name !== original.crop_name) {
   payload.crop_name = formData.crop_name
  }
  if (formData.crop_category !== original.crop_category) {
   payload.crop_category = formData.crop_category
  }
  const hsnCode = formData.hsn_code || ''
  if (hsnCode !== original.hsn_code) {
   payload.hsn_code = hsnCode || null
  }
  const lingo = formData.lingo || ''
  if (lingo !== original.lingo) {
   payload.lingo = lingo || null
  }
  const currentImageUrl =
   uploadedFileData?.crop_image?.filePath ?? null
  if (currentImageUrl !== original.image_url) {
   payload.image_url = currentImageUrl
  }

  const currentVarieties = getVarietiesPayload(formData)
  if (
   JSON.stringify(currentVarieties) !==
   original.varietiesJSON
  ) {
   payload.varieties = currentVarieties
  }

  const currentQualityParameters =
   getQualityParametersPayload()
  if (
   JSON.stringify(currentQualityParameters) !==
   original.qualityParametersJSON
  ) {
   payload.quality_parameters = currentQualityParameters
  }

  return payload
 }

 const submitFormData = async (formData: IFormField) => {
  if (!validateBeforeSubmit()) return
  setLoader(true)
  if (isEditMode && cropId) {
   const updatePayload = buildUpdatePayload(formData)
   if (Object.keys(updatePayload).length === 0) {
    setLoader(false)
    navigate(getCropMasterPath())
    return
   }
   const res = await updateCrop(cropId, updatePayload)
   if (res) {
    navigate(getCropMasterPath())
   }
  } else {
   const createPayload = buildCreatePayload(formData)
   const res = await createCrop(createPayload)
   if (res) {
    navigate(getCropMasterPath())
   }
  }
  setLoader(false)
 }

 const pageTitle = isEditMode
  ? t('cropMaster.addCrop.editTitle')
  : t('cropMaster.addCrop.title')

 const hasUploadedImage = Boolean(
  uploadedFileData?.crop_image?.signedUrl
 )

 return (
  <>
   {loader && <Spinner />}
   <div className='mb-2 flex items-start justify-between gap-4'>
    <div>
     <p className='text-[#171B1F] text-xl font-bold'>
      {pageTitle}
     </p>
     <p className='text-[#80868C] text-sm mt-1'>
      {t('cropMaster.addCrop.subtitle')}
     </p>
    </div>
    {isDisabled ? (
     <Button
      type='button'
      disabled={false}
      onClick={handleEditBtnClick}
      color='blue'
      className='text-center py-[9px] px-6 bg-[#4E83E4]
       !rounded-lg text-base text-white hover:bg-white hover:!text-[#4E83E4] font-semibold border-[#4E83E4] shrink-0'
     >
      {t('common.edit')}
     </Button>
    ) : (
     <Button
      type='button'
      disabled={!isValid}
      onClick={handleSubmit(submitFormData)}
      color='blue'
      className='disabled:text-white text-center py-[9px] px-6 bg-[#4E83E4]
       !rounded-lg text-base text-white hover:bg-white hover:!text-[#4E83E4] font-semibold border-[#4E83E4] shrink-0'
     >
      {t('common.save')}
     </Button>
    )}
   </div>
   <div className='border-b border-[#E6EBF0] my-5' />

   <form
    method='POST'
    action=''
    onSubmit={handleSubmit(submitFormData)}
   >
    <div className={SECTION_CARD_CLASS}>
     <p className='text-[#171B1F] text-base font-bold mb-4'>
      {t('cropMaster.addCrop.cropDetails')}
     </p>
     <div className='grid md:grid-cols-3 gap-5'>
      <TextField
       type='text'
       label={t('cropMaster.addCrop.cropName')}
       labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
       wrapperClassName='relative w-full'
       inputWrapperClassName={getFieldInputWrapperClass(
        Boolean(watchedCropName)
       )}
       className={getFilledStateClass(
        Boolean(watchedCropName)
       )}
       required={true}
       disabled={isEditMode}
       id='crop_name'
       name='crop_name'
       register={register('crop_name', {
        required: `${t('helperText.plsEnter')} ${t(
         'cropMaster.addCrop.cropName'
        )}`,
        maxLength: {
         value: 100,
         message: `${t('cropMaster.addCrop.cropName')} ${t(
          'helperText.maxLength',
          {max: 100}
         )}`,
        },
       })}
       placeholder={t(
        'cropMaster.addCrop.cropNamePlaceholder'
       )}
       errors={errors}
      />
      <TextField
       type='text'
       label={t('cropMaster.addCrop.lingo')}
       labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
       wrapperClassName='relative w-full'
       inputWrapperClassName={getFieldInputWrapperClass(
        Boolean(watchedLingo)
       )}
       className={getFilledStateClass(
        Boolean(watchedLingo)
       )}
       disabled={isDisabled}
       id='lingo'
       name='lingo'
       register={register('lingo', {
        maxLength: {
         value: 500,
         message: `${t('cropMaster.addCrop.lingo')} ${t(
          'helperText.maxLength',
          {max: 500}
         )}`,
        },
       })}
       placeholder={t(
        'cropMaster.addCrop.lingoPlaceholder'
       )}
       errors={errors}
      />
      <Select2DropdownField
       label={t('cropMaster.addCrop.cropCategory')}
       labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
       className='boxed-select2 !font-normal relative block text-lg border-0 !rounded-l-lg'
       wrapperClassName='relative w-full'
       inputWrapperClassName={`${getFieldInputWrapperClass(
        Boolean(watchedCropCategory)
       )} pl-4 py-2 px-4`}
       customStyle={SELECT_FIELD_RESET_STYLE}
       styles={{
        placeholder: (base: CSSObjectWithLabel) => ({
         ...base,
         color: '#80868C',
        }),
       }}
       required={true}
       disabled={isEditMode}
       isClearable={false}
       isSearchable={true}
       control={control}
       data={cropCategoryOptions}
       id='crop_category'
       name='crop_category'
       OptionValueKey='id'
       OptionLabelKey='name'
       rules={{
        validate: {
         required: (v: string) => {
          if (!v)
           return `${t('helperText.plsSelect')} ${t(
            'cropMaster.addCrop.cropCategory'
           )}`
         },
        },
       }}
       placeholder={t(
        'cropMaster.addCrop.cropCategoryPlaceholder'
       )}
       errors={errors}
      />
      <TextField
       type='text'
       label={t('cropMaster.addCrop.hsnCode')}
       labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
       wrapperClassName='relative w-full'
       inputWrapperClassName={getFieldInputWrapperClass(
        Boolean(watchedHsnCode)
       )}
       className={getFilledStateClass(
        Boolean(watchedHsnCode)
       )}
       disabled={isDisabled}
       id='hsn_code'
       name='hsn_code'
       register={register('hsn_code', {
        pattern: {
         value: HSN_CODE_PATTERN,
         message: t('cropMaster.addCrop.hsnCodeError'),
        },
       })}
       placeholder={t(
        'cropMaster.addCrop.hsnCodePlaceholder'
       )}
       errors={errors}
      />
     </div>
    </div>

    <div className={`${SECTION_CARD_CLASS} mt-5`}>
     <p className='text-[#171B1F] text-base font-bold'>
      {t('cropMaster.addCrop.varieties')}
     </p>
     <p className='text-[#80868C] text-sm mt-1 mb-4'>
      {t('cropMaster.addCrop.varietiesSubtitle')}
     </p>
     {varietyFields.length > 0 && (
      <div className='flex flex-wrap gap-3 mb-4'>
       {varietyFields.map((field, index) => {
        const watchedVarietyName = watch(
         `varieties.${index}.name`
        )

        return (
         <div key={field.id}>
          <TextField
           type='text'
           label={t('cropMaster.addCrop.varietyName')}
           labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
           wrapperClassName='relative w-[280px]'
           inputWrapperClassName={getVarietyFieldInputWrapperClass(
            Boolean(watchedVarietyName)
           )}
           className={getFilledStateClass(
            Boolean(watchedVarietyName)
           )}
           showIcon={false}
           disabled={isDisabled}
           id={`varieties.${index}.name`}
           name={`varieties.${index}.name`}
           register={register(`varieties.${index}.name`)}
           placeholder={t(
            'cropMaster.addCrop.varietyNamePlaceholder'
           )}
           errors={errors}
           endAdornment={
            !isDisabled && (
             <button
              type='button'
              onClick={() => handleRemoveVariety(index)}
              className='absolute bottom-[6px] right-[6px] flex items-center justify-center w-5 h-5 rounded-md hover:bg-[#FDE9EB]'
             >
              <KTSVG
               path='/media/icons/trash.svg'
               svgClassName='w-3.5 h-3.5'
              />
             </button>
            )
           }
          />
         </div>
        )
       })}
      </div>
     )}
     <Button
      type='button'
      disabled={isDisabled}
      onClick={handleAddVariety}
      color='blue'
      className='text-center py-[9px] px-6 bg-[#F5FAFF]
       !rounded-lg text-base !text-[#4E83E4] font-semibold border-[#4E83E4] w-fit disabled:!text-[#80868C] disabled:!border-[#CED8E0] disabled:cursor-not-allowed'
     >
      + {t('cropMaster.addCrop.addVariety')}
     </Button>
    </div>

    <div className={`${SECTION_CARD_CLASS} mt-5`}>
     <p className='text-[#171B1F] text-base font-bold'>
      {t('cropMaster.addCrop.qualityConfiguration')}
     </p>
     <p className='text-[#80868C] text-sm mt-1 mb-4'>
      {t('cropMaster.addCrop.qualityConfigurationSubtitle')}
     </p>
     <table className='w-full mb-4'>
      <thead>
       <tr className='bg-[#80868C] text-left text-xs text-white'>
        <th className='py-2 px-3 font-medium'>
         {t('cropMaster.addCrop.parameterName')}
        </th>
        <th className='py-2 px-3 font-medium'>
         {t('cropMaster.addCrop.acceptableRange')}
        </th>
        <th className='py-2 px-3 w-[110px]' />
       </tr>
      </thead>
      <tbody>
       {qualityParameters.map((qp, index) => (
        <tr
         key={`${qp.quality_parameter_id}-${index}`}
         className='border-b border-[#E6EBF0] text-sm text-[#33393D]'
        >
         <td className='py-2 px-3'>
          {qp.quality_parameter_name}
         </td>
         <td className='py-2 px-3'>
          {formatParameterValue(qp)}
         </td>
         <td className='py-2 px-3 text-right'>
          <div className='flex items-center justify-end gap-4'>
           <button
            type='button'
            disabled={isDisabled}
            onClick={() =>
             handleOpenEditQualityParameter(index)
            }
            className={`!bg-transparent border-0 text-sm font-medium underline ${
             isDisabled
              ? '!text-[#80868C] cursor-not-allowed'
              : '!text-[#4E83E4]'
            }`}
           >
            {t('common.edit')}
           </button>
           <button
            type='button'
            disabled={isDisabled}
            onClick={() =>
             handleDeleteQualityParameterClick(index)
            }
            className={`!bg-transparent border-0 text-sm font-medium underline ${
             isDisabled
              ? '!text-[#80868C] cursor-not-allowed'
              : '!text-[#CC293F]'
            }`}
           >
            {t('common.delete')}
           </button>
          </div>
         </td>
        </tr>
       ))}
      </tbody>
     </table>
     <Button
      type='button'
      disabled={
       isDisabled ||
       qualityParameters.length >= MAX_QUALITY_PARAMETERS
      }
      onClick={handleOpenAddQualityParameter}
      color='blue'
      className='text-center py-[9px] px-6 bg-[#F5FAFF]
       !rounded-lg text-base !text-[#4E83E4] font-semibold border-[#4E83E4] w-fit disabled:!text-[#80868C] disabled:!border-[#CED8E0] disabled:cursor-not-allowed'
     >
      + {t('cropMaster.addCrop.configureQualityParameters')}
     </Button>
    </div>

    {showQualityParameterModal && (
     <QualityParameterModal
      show={showQualityParameterModal}
      excludedParameterIds={qualityParameters
       .filter((_, i) => i !== editingQualityParameterIndex)
       .map((qp) => qp.quality_parameter_id)}
      editingParameter={
       editingQualityParameterIndex !== null
        ? qualityParameters[editingQualityParameterIndex]
        : null
      }
      close={handleCloseQualityParameterModal}
      onSave={handleSaveQualityParameters}
      setLoader={setLoader}
     />
    )}

    {pendingDeleteQualityParameterIndex !== null && (
     <KTConfirmPopup
      showConfirmModel={
       pendingDeleteQualityParameterIndex !== null
      }
      handleConfirmBtnClick={(confirmed) => {
       void handleConfirmDeleteQualityParameter(confirmed)
      }}
      yesBtnText={t('common.yes')}
      noBtnText={t('common.no')}
      title={t('common.confirmModalTitle')}
      subTitle={`${t('common.confirmModalSubTitle')}${t(
       'common.delete'
      ).toLowerCase()} ${t(
       'cropMaster.addCrop.deleteQualityParameterConfirm'
      )}`}
     />
    )}

    <div className={`${SECTION_CARD_CLASS} mt-5`}>
     <FileField
      name='crop_image'
      id='crop_image'
      disabled={isDisabled}
      signedPhoto={uploadedFileData?.crop_image?.signedUrl}
      fileType={uploadedFileData?.crop_image?.fileType}
      fileName={uploadedFileData?.crop_image?.fileName}
      fileSize={uploadedFileData?.crop_image?.fileSize}
      uploadHintText={t('cropMaster.addCrop.uploadImage')}
      validFileText={t(
       'cropMaster.addCrop.imageUploadHint'
      )}
      uploadedActionsBox={
       hasUploadedImage && !isDisabled ? (
        <button
         type='button'
         onClick={handleRemoveCropImage}
         className='inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#D7DFEA] bg-white text-[#5A6066] cursor-pointer shrink-0 hover:bg-[#FDE9EB] hover:text-[#CC293F]'
         aria-label={t('common.remove')}
        >
         <KTSVG
          path='/media/icons/trash.svg'
          svgClassName='w-4 h-4 shrink-0'
         />
        </button>
       ) : undefined
      }
      onChange={(e) => {
       void handleFileChange(
        e,
        uploadedFileData,
        setLoader,
        setUploadedFileData,
        'crop_image',
        'crop',
        'public'
       )
      }}
     />
    </div>
   </form>
  </>
 )
}

export default function AddCropPageWrapper(props: IProps) {
 const {t} = useTranslation()

 return (
  <div className='container relative'>
   <div className='px-[72px] pt-6 pb-4 bg-white w-full min-h-full'>
    <PageTitle
     breadcrumbContainerClass='flex text-[#5A6066] text-sm font-normal items-center pb-6 border-[#E6EBF0] border-b mb-4'
     breadcrumbs={[
      {
       title: t('breadCrumbs.home'),
       path: '/home-page',
       isActive: false,
      },
      {
       title: t('breadCrumbs.cropMaster'),
       path: getCropMasterPath(),
       isActive: false,
      },
      {
       title: props.cropId
        ? t('cropMaster.addCrop.editTitle')
        : t('cropMaster.addCrop.title'),
       path: '',
       isActive: true,
      },
     ]}
     description='Welcome'
    >
     {props.cropId
      ? t('cropMaster.addCrop.editTitle')
      : t('cropMaster.addCrop.title')}
    </PageTitle>
    <AddCropPage {...props} />
   </div>
  </div>
 )
}
